<template>
  <div>
    <breadcrumb-header
      v-if="withBreadcrumbs"
      :items="breadcrumbs"
    />
    <div
      v-if="(loadingCount > 0) || !shift"
      class="my-5"
    >
      <loading-spinner />
    </div>
    <div v-else>
      <v-alert
        v-if="!shift.display_contacts"
        type="info"
      >
        {{ t('shift.detail.hint', { count: contactsVisibleForDays }, contactsVisibleForDays) }}
      </v-alert>

      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <global-card
            class="mt-3"
            :title="t('global.details')"
            :with-actions="false"
            fill-height
          >
            <template #image>
              <v-img
                :src="locationHeroImage"
                class="align-end"
                cover
              >
                <v-card-title class="text-white">
                  {{ shift?.location?.name }}
                  <br>
                  {{ toLongDateWithDay(shift.date) }}
                </v-card-title>
              </v-img>
            </template>
            <div class="mb-1 text-font">
              <v-icon
                color="primary"
                :icon="`fas ${shift.previous ? 'fa-arrow-circle-down' : 'fa-arrow-circle-right'}`"
              />
              <v-icon
                color="primary"
                icon="fas fa-suitcase-rolling"
              />
              {{ t('shift.before') }}: {{ shift.previous ? t('global.yes') : t('global.no') }}
            </div>
            <div class="mb-1 text-font">
              <v-icon icon="far fa-clock" />
              {{ shiftTime }}
            </div>
            <div class="mb-1 text-font">
              <v-icon
                color="primary"
                :icon="`fas ${shift.next ? 'fa-arrow-circle-down' : 'fa-arrow-circle-left'}`"
              />
              <v-icon
                color="primary"
                icon="fas fa-suitcase-rolling"
              />
              {{ t('shift.next') }}: {{ shift.next ? t('global.yes') : t('global.no') }}
            </div>
          </global-card>
        </v-col>
        <v-col
          v-for="(dayOverseer) in dayOverseers"
          :key="dayOverseer.id"
          cols="12"
          md="6"
        >
          <member-card
            class="mt-3"
            :user="dayOverseer"
            :title="`${dayOverseer.abbreviated_order_name} ${getSetting('day_overseer_display_type') === 'day' ? t('dayoverseer.title') : t('dayoverseer.location_overseer')}`"
            :show-email="false"
            fill-height
            :zoom-enabled="true"
          />
        </v-col>
        <v-col
          v-if="shift.weather_forecast"
          cols="12"
          md="4"
        >
          <global-card
            :title="t('system_info.weather_forecast')"
            class="mt-3 text-center"
            :with-actions="false"
          >
            <div class="d-flex flex-column">
              <div class="text-uppercase text-font">
                <v-icon
                  class="mt-2 me-2"
                  :icon="weatherIconClass(shiftWeatherCondition)"
                />
                <div>
                  {{ shift.weather_forecast.condition_desc }}
                </div>
              </div>
              <div>
                <v-icon
                  icon="fas fa-thermometer-half"
                  class="text-font me-2"
                /> {{ formatTemperature(shift.weather_forecast.temperature) }}
              </div>
              <div>
                <v-icon
                  icon="fas fa-wind"
                  class="me-2 text-font"
                /><span class="text-font"> {{ formatWindSpeed(shift.weather_forecast.wind_speed) }}</span>
              </div>
            </div>
          </global-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <global-card
            :title="t('shift.detail.important_notes')"
            :with-actions="false"
          >
            <v-card-text v-if="shift?.tags?.length">
              <shift-tag
                v-for="(tag, index) in shift.tags"
                :key="index"
                size="large"
                class="mb-2"
                :shift-tag="tag"
              />
            </v-card-text>
            <v-list>
              <v-list-item
                v-if="userIsCaptain || userIsAssistant"
                class="wrap-list"
                :title="t('shift.detail.captain_hint_1', { captain: label })"
              >
                <div class="mb-2">
                  {{ t('shift.detail.captain_hint_2', { captain: label }) }}
                </div>
                <v-list-item-action>
                  <v-dialog
                    v-if="duties"
                    v-model="dutiesDialog"
                    :width="xs ? undefined : '1200px'"
                    :fullscreen="xs"
                  >
                    <template #activator="{ props: activatorProps }">
                      <v-btn
                        v-bind="activatorProps"
                        color="primary"
                        :text="t('shift.detail.duties', { captain: label })"
                      />
                    </template>
                    <global-card
                      :title="t('shift.detail.duties', { captain: label })"
                      closable
                      @close="dutiesDialog = false"
                    >
                      <rich-text-content
                        :data="duties"
                      />
                    </global-card>
                  </v-dialog>
                </v-list-item-action>
              </v-list-item>
              <v-list-item
                v-if="shift.location?.carts?.length"
                :title="t('shift.detail.carts_title')"
              >
                <cart-list :carts="shift.location.carts" />
              </v-list-item>
              <v-list-item
                v-if="!shift.previous"
                :title="t('shift.detail.collect_cart_title')"
              >
                {{ t('shift.detail.collect_cart_hint') }}
              </v-list-item>
              <v-list-item
                v-if="shift.previous"
                :title="t('shift.detail.previous_shift_title')"
              >
                {{ t('shift.detail.previous_shift_hint') }}
              </v-list-item>
              <v-list-item
                v-if="!shift.next"
                :title="t('shift.detail.return_cart_title')"
              >
                {{ t('shift.detail.return_cart_hint') }}
              </v-list-item>
              <v-list-item
                v-if="shift.next"
                :title="t('shift.detail.next_shift_title')"
              >
                {{ t('shift.detail.next_shift_hint') }}
              </v-list-item>
            </v-list>
          </global-card>
        </v-col>
        <template v-if="shift.display_contacts">
          <v-col
            v-if="shift.previous?.captain && userIsCaptain"
            cols="6"
            md="4"
          >
            <member-card
              class="mt-3"
              :title="t('global.previous_captain', { captain: (getSetting('captain_label') ?? '').toLowerCase() })"
              :user="shift.previous.captain"
              :show-email="false"
            />
          </v-col>
          <v-col
            v-if="shift.next?.captain && userIsCaptain"
            cols="6"
            md="4"
          >
            <member-card
              class="mt-3"
              :title="t('global.next_captain', { captain: (getSetting('captain_label') ?? '').toLowerCase() })"
              :user="shift.next.captain"
              :show-email="false"
            />
          </v-col>

          <v-col cols="12">
            <global-card
              :title="t('shift.detail.contacts', 2)"
              :with-actions="false"
            >
              <v-container fluid>
                <v-row
                  class="mt-0 mx-n5"
                  density="compact"
                >
                  <v-col
                    v-for="(publisher, index) in (shift?.publishers ?? []).filter(publisher => publisher.id == shift?.captain?.id)"
                    :key="index"
                    cols="6"
                    md="4"
                    class="pb-0"
                  >
                    <member-card
                      :user="publisher"
                      :show-email="false"
                      show-heading
                      :title="getSetting('captain_label') ?? ''"
                      :zoom-enabled="true"
                    />
                  </v-col>
                  <v-col
                    v-for="(publisher, index) in [...(shift?.publishers ?? [])].filter(publisher => publisher.id != shift?.captain?.id).sort((a, b) => (a.is_assistant === true ? 0 : 1) - (b.is_assistant === true ? 0 : 1))"
                    :key="index"
                    cols="6"
                    md="4"
                    class="pb-0"
                  >
                    <member-card
                      :user="publisher"
                      :show-email="false"
                      :show-heading="publisher.is_assistant"
                      :title="t('shift.assistant')"
                      fill-height
                      :zoom-enabled="true"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </global-card>
          </v-col>
        </template>
      </v-row>

      <v-divider class="my-5" />

      <location-details
        v-if="shift.location"
        :show-map="showMap"
        :location-id="shift.location.id"
        :shift-id="(shiftIsTodayOrTomorrow) ? shift.id : undefined"
        :show-breadcrumbs="false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import {
  computed,
  onBeforeMount,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import GlobalCard from '@/components/globals/GlobalCard.vue';
import RichTextContent from '@/components/rich-text/RichTextContent.vue';
import { useAuth, useDate, useSettings, useWeather } from '@/composables';
import { useErrorStore } from '@/stores/errors';
import CartList from './carts/CartList.vue';
import LocationDetails from './locations/LocationDetails.vue';
import ShiftTag from './ShiftTag.vue';
import MemberCard from './teams/MemberCard.vue';

const props = withDefaults(defineProps<{
  shiftId: number | string;
  withBreadcrumbs?: boolean;
  showMap?: boolean;
}>(), {
  withBreadcrumbs: () => true,
  showMap: () => true,
});
const { xs } = useDisplay();
const { t } = useI18n();
const { getSetting } = useSettings();
const dayOverseerType = getSetting('day_overseer_display_type');

const { handleError } = useErrorStore();

const breadcrumbs = computed(() => [
  {
    title: t('shift.details'),
  },
]);

const {
  toShortTime,
  toLongDateWithDay,
  isTodayOrTomorrow,
  contactsVisibleForDays,
  getIsoWeekday,
} = useDate();

const { authUser } = useAuth();
const { weatherIconClass, formatTemperature, formatWindSpeed } = useWeather();

const shift = ref<App.ApiResources.ShiftResource>();

/**
 * The forecast row carried by the shift predates the day view's condition shape;
 * adapt it so both pages draw their icon through the same helper.
 */
const shiftWeatherCondition = computed(() => {
  const forecast = shift.value?.weather_forecast;
  if (!forecast) {
    return null;
  }

  return {
    id: Number(forecast.condition_id),
    description: forecast.condition_desc,
    icon: forecast.condition_icon,
    is_night: Boolean(forecast.is_night),
  } as App.ApiResources.WeatherCondition;
});

const dutiesDialog = ref(false);
const duties = ref('');
const loadingCount = ref(0);

const shiftIsTodayOrTomorrow = computed(() => isTodayOrTomorrow(`${shift.value?.date}T00:00`));

const shiftTime = computed(() => {
  if (shift.value) {
    return `${shift.value?.band?.name} ${toShortTime(shift.value?.begins)} - ${toShortTime(shift.value?.ends)}`;
  }
  return null;
});

const locationHeroImage = computed(() => shift.value?.location?.hero_image?.signed_file_url ?? '/storage/images/defaut_location_hero.jpg');

const userIsCaptain = computed(() => shift.value?.captain?.id === authUser.id);
const userIsAssistant = computed(() => shift.value?.assigned_publishers?.some(p => p.is_assistant && p.id === authUser.id));

const label = computed(() => {
  if (userIsCaptain.value) {
    return getSetting('captain_label');
  }
  return t('shift.shift_assistant');
});

async function loadShiftDetails() {
  loadingCount.value += 1;
  try {
    const response = await axios.get(`/api/shifts/${props.shiftId}`);
    shift.value = response.data.data;
  }
  catch (error) {
    handleError(error, t('shift.detail.cannot_load'));
  }
  finally {
    loadingCount.value -= 1;
  }
}

async function loadDuties() {
  if (duties.value) {
    return;
  }

  let dutiesId;
  if (userIsCaptain.value) {
    dutiesId = getSetting('captain_duties_faq_id');
  }
  else if (userIsAssistant.value) {
    dutiesId = getSetting('assistant_duties_faq_id');
  }
  if (!dutiesId) {
    return;
  }
  try {
    const response = await axios.get(`/api/public/faqs/${dutiesId}`);
    duties.value = response.data?.answer;
  }
  catch (error) {
    handleError(error, t('shift.detail.cannot_load_duties'));
  }
}

onBeforeMount(() => {
  loadShiftDetails();
});

watch(() => shift.value?.captain, () => {
  if (userIsCaptain.value) {
    loadDuties();
  }
});

watch(() => shift.value?.assigned_publishers, () => {
  if (userIsAssistant.value) {
    loadDuties();
  }
});

const dayOverseers = computed<App.Models.DayOverseer[]>(() => {
  const shiftOverseers = shift.value?.day_overseers ?? [];
  if (dayOverseerType === 'day') {
    return shiftOverseers;
  }

  const locationOverseers = shift.value?.location?.day_overseers ?? [];
  const holidayOverseers = locationOverseers.filter((overseer) => overseer.day_of_week === 0);
  if (shift.value?.is_holiday && holidayOverseers.length > 0) {
    return holidayOverseers;
  }
  const isoWeekday = getIsoWeekday(shift.value?.date);
  return locationOverseers.filter((overseer) => overseer.day_of_week === isoWeekday);
});
</script>
